import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { getHub } from '@/services/signalr';
import { gpsTracking } from '@/services/gpsTracking';
import { ActiveSelectiveItem } from './ActiveSelectiveItem';
import { REFETCH_LIST_MS } from '@/shared/timing';
import type { ActiveSelective } from './types';

/**
 * Modal "Selettive attive": lista in real-time delle selettive aperte nel
 * tenant, refetch ogni 15s + invalidazione su SignalR. Per ciascuna mostra
 * un ActiveSelectiveItem con counter, GPS, accetta/rifiuta/termina.
 */
export function ActiveSelectivesModal({ onClose }: { onClose: () => void }): React.JSX.Element {
  const qc = useQueryClient();

  const { data: active, isLoading } = useQuery<ActiveSelective[]>({
    queryKey: ['selectives.active'],
    queryFn: async () => (await api.get('/selectives/active')).data,
    refetchInterval: REFETCH_LIST_MS,
  });

  // Auto-refresh quando arrivano eventi SignalR + stop GPS tracking se la
  // selettiva trackata viene terminata
  useEffect(() => {
    const conn = getHub('/api/hubs/selective');
    const onChange = () => qc.invalidateQueries({ queryKey: ['selectives.active'] });
    const onTerm = (evt: { selectiveId: string }) => {
      onChange();
      if (gpsTracking.getActive() === evt.selectiveId) gpsTracking.stop();
    };
    conn.on('selectiveCreated', onChange);
    conn.on('selectiveResponse', onChange);
    // "Non piu' necessario": la selettiva resta attiva, ma il flag
    // noLongerNeededAt cambia -> refresh per disabilitare il pulsante anche
    // per gli altri admin che hanno il modal aperto. NESSUNO stop GPS.
    conn.on('selectiveNoLongerNeeded', onChange);
    conn.on('selectiveTerminated', onTerm);
    return () => {
      conn.off('selectiveCreated', onChange);
      conn.off('selectiveResponse', onChange);
      conn.off('selectiveNoLongerNeeded', onChange);
      conn.off('selectiveTerminated', onTerm);
    };
  }, [qc]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}
        style={{ maxWidth: 700, padding: 30 }}>
        <h3 style={{ marginBottom: 16, fontSize: 22 }}>📋 Selettive Attive</h3>

        <div style={{ maxHeight: 450, overflowY: 'auto', marginBottom: 15 }}>
          {isLoading && <p style={{ textAlign: 'center' }}>Caricamento...</p>}
          {!isLoading && (active?.length ?? 0) === 0 && (
            <p style={{ textAlign: 'center', color: '#6c757d', padding: 30 }}>
              Nessuna selettiva attiva al momento
            </p>
          )}
          {active?.map(s => <ActiveSelectiveItem key={s.id} s={s} />)}
        </div>

        <button onClick={onClose}
          style={{
            width: '100%', padding: 12, fontSize: 16, fontWeight: 'bold',
            border: 'none', borderRadius: 8, cursor: 'pointer',
            background: '#6c757d', color: 'white',
          }}>
          Chiudi
        </button>
      </div>
    </div>
  );
}
